import { useState, type FormEvent } from "react";
import {
  API_ROUTES,
  LIMITS,
  type Comment,
  type CommentSort,
} from "@ziolo/shared";
import { Button, Card, useFlash } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useComments } from "../hooks/useComments.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSession } from "../session/SessionProvider.js";
import { formatTimestamp } from "../lib/format.js";
import { Field } from "./Field.js";

const SORT_OPTIONS: ReadonlyArray<{ value: CommentSort; label: string }> = [
  { value: "new", label: "Najnowsze" },
  { value: "old", label: "Najstarsze" },
  { value: "top", label: "Najwyżej oceniane" },
  { value: "bottom", label: "Najniżej oceniane" },
];

export function CommentsPanel() {
  const { user } = useSession();
  const comments = useComments();

  return (
    <>
      {user ? <CommentForm onCreated={comments.add} /> : null}

      <section className="hub-feed">
        <header className="hub-feed__header">
          <h3 className="hub-feed__title">
            <i className="z-icon" aria-hidden="true">
              💬
            </i>
            Komentarze
          </h3>
          <div className="hub-sort">
            <label className="hub-hint" htmlFor="comment-sort">
              Sortuj:
            </label>
            <select
              id="comment-sort"
              className="z-select hub-sort__select"
              value={comments.sort}
              onChange={(event) => comments.setSort(event.target.value as CommentSort)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </header>

        {comments.error ? <p className="hub-error">{comments.error}</p> : null}

        {comments.loading ? (
          <p className="hub-hint">Wczytywanie komentarzy...</p>
        ) : comments.items.length === 0 ? (
          <p className="hub-hint">Nikt jeszcze nic nie napisał.</p>
        ) : (
          <div className="hub-comments">
            {comments.items.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                canVote={user !== null}
                onVote={comments.vote}
                onDelete={comments.remove}
              />
            ))}
          </div>
        )}

        {comments.hasMore ? (
          <Button
            block
            variant="ghost"
            className="hub-load-more"
            disabled={comments.loadingMore}
            onClick={() => void comments.loadMore()}
          >
            {comments.loadingMore
              ? "Wczytywanie..."
              : `Pokaż więcej (${comments.items.length}/${comments.total})`}
          </Button>
        ) : null}
      </section>
    </>
  );
}

function CommentForm({ onCreated }: { onCreated: (comment: Comment) => Promise<void> }) {
  const { busy, fields, submit } = useFormSubmit();
  const [body, setBody] = useState("");

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    const ok = await submit(async () => {
      const created = await apiRequest<Comment>(API_ROUTES.comments.create, {
        method: "POST",
        body: { body },
      });
      await onCreated(created);
    }, "Komentarz dodany");

    if (ok) setBody("");
  }

  const remaining = LIMITS.comment.max - body.length;

  return (
    <Card variant="sunken" title="Dodaj komentarz" icon="✍️">
      <form className="hub-form" onSubmit={onSubmit}>
        <Field
          error={fields.body}
          hint={`Pozostało ${remaining} z ${LIMITS.comment.max} znaków`}
        >
          <textarea
            className="z-textarea"
            name="body"
            placeholder="Twój komentarz..."
            maxLength={LIMITS.comment.max}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            required
          />
        </Field>
        <div className="hub-form__actions">
          <Button type="submit" variant="primary" disabled={busy || body.trim() === ""}>
            {busy ? "Wysyłanie..." : "Dodaj"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface CommentItemProps {
  comment: Comment;
  canVote: boolean;
  onVote: (id: number, vote: 1 | -1) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

function CommentItem({ comment, canVote, onVote, onDelete }: CommentItemProps) {
  const { push } = useFlash();
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (cause) {
      push(errorMessage(cause), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="hub-comment">
      <header className="hub-comment__meta">
        <span className="hub-comment__author">{comment.username}</span>
        <time dateTime={comment.createdAt}>{formatTimestamp(comment.createdAt)}</time>
      </header>

      <p className="hub-comment__body">{comment.body}</p>

      <footer className="hub-votes">
        <VoteButton
          direction="up"
          count={comment.likes}
          active={comment.userVote === 1}
          disabled={!canVote || busy}
          onClick={() => void run(() => onVote(comment.id, 1))}
        />
        <VoteButton
          direction="down"
          count={comment.dislikes}
          active={comment.userVote === -1}
          disabled={!canVote || busy}
          onClick={() => void run(() => onVote(comment.id, -1))}
        />

        {!canVote ? (
          <span className="hub-hint">Zaloguj się, aby głosować</span>
        ) : null}

        {comment.canDelete ? (
          <Button
            variant="danger"
            size="sm"
            className="hub-votes__delete"
            disabled={busy}
            onClick={() => {
              if (!window.confirm("Usunąć ten komentarz?")) return;
              void run(() => onDelete(comment.id));
            }}
          >
            Usuń
          </Button>
        ) : null}
      </footer>
    </article>
  );
}

interface VoteButtonProps {
  direction: "up" | "down";
  count: number;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}

function VoteButton({ direction, count, active, disabled, onClick }: VoteButtonProps) {
  const up = direction === "up";

  return (
    <button
      type="button"
      className={`hub-vote ${up ? "hub-vote--up" : "hub-vote--down"}${active ? " hub-vote--active" : ""}`}
      aria-label={up ? "Lubię to" : "Nie lubię"}
      aria-pressed={active}
      title={disabled ? "Zaloguj się, aby głosować" : "Kliknij ponownie, aby cofnąć"}
      disabled={disabled}
      onClick={onClick}
    >
      {up ? "▲" : "▼"} <span>{count}</span>
    </button>
  );
}
